import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';
import { useSiteSection } from '@/hooks/useSiteSection';

const ContactPage = () => {
  const navigate = useNavigate();
  const page = useSiteSection('page_contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message envoyé avec succes ! Nous vous repondrons dans les plus brefs delais.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    page?.email && { icon: Mail, title: 'Email', content: page.email, link: `mailto:${page.email}` },
    page?.phone && { icon: Phone, title: 'Téléphone', content: page.phone, link: `tel:${page.phone.replace(/\s/g, '')}` },
    page?.address && { icon: MapPin, title: 'Adresse', content: page.address, link: null },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />
      <SEO title="Contact" description="Contactez la SFOALC. Notre équipe est à votre écoute pour toute question concernant l'association, les adhésions ou les formations." />
      <div className="h-[72px]" />

      {/* Hero Section */}
      <section className="pt-12 pb-16 px-4" style={{ background: '#eaf6fc' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" data-testid="contact-title">
            {page?.title}
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed" data-testid="contact-subtitle">
            {page?.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className={`grid gap-8 mb-12 ${contactInfo.length >= 3 ? 'lg:grid-cols-3' : 'sm:grid-cols-2 max-w-3xl mx-auto'}`}>
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#35AFE4] transition-colors" data-testid={`contact-info-${info.title.toLowerCase()}`}>
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#eaf6fc] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-[#35AFE4]" size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {info.title}
                    </h3>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className="text-gray-600 hover:text-[#1e3a5f] transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-gray-600">{info.content}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-2">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Envoyez-nous un message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.fr"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 12 34 56 78"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject">Sujet *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Objet de votre message"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Votre message..."
                      rows={6}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#35AFE4] hover:bg-[#2d7d9e] flex items-center justify-center gap-2 rounded-full"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? 'Envoi en cours...' : (
                      <>
                        <Send size={18} />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <div className="space-y-6">
              {page?.hours && (
                <Card className="bg-gradient-to-br from-[#eaf6fc] to-blue-50 border-2 border-[#35AFE4]/30" data-testid="contact-hours">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Horaires d'ouverture
                    </h3>
                    <p className="text-gray-700 whitespace-pre-line">{page.hours}</p>
                  </CardContent>
                </Card>
              )}

              <Card className="border-2">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Questions Fréquentes
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Avant de nous contacter, consultez notre FAQ pour trouver rapidement des réponses à vos questions.
                  </p>
                  <Button
                    onClick={() => navigate('/faq')}
                    variant="outline"
                    className="w-full border-[#35AFE4] text-[#35AFE4] hover:bg-[#eaf6fc] rounded-full"
                    data-testid="contact-faq-btn"
                  >
                    Consulter la FAQ
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Rejoignez-nous
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Vous êtes ophtalmologiste et souhaitez devenir membre de la SFOALC ?
                  </p>
                  <Button
                    onClick={() => navigate('/adhesion')}
                    className="w-full bg-[#35AFE4] hover:bg-[#2d7d9e] rounded-full"
                    data-testid="contact-adhesion-btn"
                  >
                    Adhérer à la SFOALC
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
